import React, { useEffect, useState, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { Button, IconButton, Link, Stack, Chip, Menu, MenuItem, ListItemIcon } from "@mui/material";
import { useTheme } from "@mui/styles";
import {
  Calendar,
  Files,
  Sparkle,
  UploadSimple,
  UserCircle,
  Pulse,
  DotsThree,
  SealCheck,
  PencilSimpleLine,
  XCircle,
  FileLock,
} from "@phosphor-icons/react";
import UploadDocumentDialog from "../../Dialogs/UploadDocumentDialog";
import { useGetClientDocuments } from "hooks/useGetClientDocuments";
import { format } from "date-fns";
import { StaticDateRangePicker } from "@mui/x-date-pickers-pro/StaticDateRangePicker";
import actions from "../../../../actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  unverifyClientDocument,
  updateDocument,
  getDocumentVerificationStatus,
  signDocumentVerification,
  getClientDocumentById,
  deleteDocument,
} from "api/customers";
import useVerifyClientDocument from "hooks/api/documents/useVerifyClientDocument";
import { DOCUMENT_TYPE_ATTESTATIONS } from "../../../../constants/documentTypes";
import { DELETE_DOCUMENT, GRANT_DOCUMENT_GENERATION_OVERRIDE } from "@constants/permissions";
import SignDocumentDialog from "components/DocumentViewer/SignDocumentDialog";
import {
  NoDocumentsWithPermissionOverlay,
  ANNoDataOverlay,
  ANNoResultsOverlay,
  ANTableFilters,
} from "@answersnow/pkg-theme";
import * as selectors from "selectors";
import noDocumentSvg from "../../../../assets/no_document.svg";
import { v4 as uuidv4 } from "uuid";
import { uploadUserDocumentToS3Async } from "api/document";
import { useGlobalToast } from "components/GlobalToastProvider";
import RecommendedTherapyHoursDialog from "components/ClientDetails/Dialogs/RecommendedTherapyHoursDialog";
import GenerateDocumentDialog from "components/ClientDetails/Dialogs/GenerateDocumentDialog";
import { useGetClientById } from "hooks/api/useGetClientById";
import DeleteDocumentDialog from "./DeleteDocumentDialog";
import DocumentSettingsDialog from "components/ClientDetails/Dialogs/DocumentSettingsDialog";
import useGetDocumentChecklistStatus from "hooks/api/documentGeneration/useGetDocumentChecklistStatus";
import type { UserPermissions } from "types/permissions";

const Documents: React.FC = () => {
  "use memo";
  const { id: clientId } = useParams<{ id: string }>();
  const { search } = useLocation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const customerDetails = useSelector(selectors.getCustomerDetails);
  const currentUser = useSelector(selectors.getUser);
  const userPermissions: UserPermissions =
    (useSelector(selectors.getUserPermissionsList) as UserPermissions | null) || ({} as UserPermissions);
  const documentTypes = useSelector(selectors.getDocumentTypes) || [];
  const uploadDocumentLoading = useSelector(selectors.getuploadDocumentLoading);
  const userId = useSelector(selectors.getUserId);

  const workflowStatus = customerDetails?.details?.workflow_status;

  const { data: clientData } = useGetClientById(clientId);
  const client = clientData?.data;

  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });
  const [uploadDocumentOpen, setUploadDocumentOpen] = useState(false);
  const [isNewDocument, setIsNewDocument] = useState(true);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [orderBy, setOrderBy] = useState("uploaded-date-desc");
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [uploadedByFilter, setUploadedByFilter] = useState<string[]>([]);
  const [uploadedDateFilter, setUploadedDateFilter] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [dropzoneFiles, setDropzoneFiles] = useState<any[]>([]);
  const [alreadyUploadedFiles, setAlreadyUploadedFiles] = useState<any[]>([]);
  const [uploadDocType, setUploadDocType] = useState("");
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [optionsMenuAnchorEl, setOptionsMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [recommendedTherapyHoursOpen, setRecommendedTherapyHoursOpen] = useState(false);
  const [generateDocumentOpen, setGenerateDocumentOpen] = useState(false);
  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [settingsMenuAnchorEl, setSettingsMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [documentSettingsOpen, setDocumentSettingsOpen] = useState(false);
  const autoOpenedVerifyIdRef = useRef<string | null>(null);
  const pendingAutoOpenIdRef = useRef<string | null>(null);

  const queryClient = useQueryClient();
  const canGrantOverride = Boolean(userPermissions[GRANT_DOCUMENT_GENERATION_OVERRIDE]);

  // Same derivation as GenerateDocumentDialog so both share one cache entry.
  const primaryClinicianId = (customerDetails as any)?.clinician?.clinician_id;
  const currentClinician =
    useSelector(selectors.getCurrentClinicianId) ||
    (currentUser as any)?.clinician_id ||
    primaryClinicianId;
  const { activeOverride, statusesLoading } = useGetDocumentChecklistStatus(
    clientId,
    currentClinician,
    { enabled: canGrantOverride }
  );

  useEffect(() => {
    const query = new URLSearchParams(search);
    const documentType = query.get("uploadDocType");
    if (documentType && documentTypes.find((doc: any) => doc.name === documentType)) {
      setUploadDocType(documentType);
      setUploadDocumentOpen(true);
    }
  }, [search, documentTypes]);

  const { showToast } = useGlobalToast();

  const { mutate: updateClientDocument, isLoading: isLoadingDocumentUpdate } = useMutation(
    (documentData: any) => updateDocument(clientId!, documentData),
    {
      onSuccess: () => updateTable(),
      onError: (error) => {
        console.error("Error updating document:", error);
      },
    }
  );

  const { verifyDocument, verifyIsLoading } = useVerifyClientDocument({
    onSuccess: () => updateTable(),
  });

  const { mutate: deleteDocumentMutation, isLoading: isDeleting } = useMutation(
    (reason: string) => deleteDocument(selectedRow.id, { reason }),
    {
      onSuccess: () => {
        showToast({ message: "Document Deleted" });
        updateTable();
        setDeleteDialogOpen(false);
        setSelectedRow(null);
      },
      onError: (error: any) => {
        showToast({ message: "Whoops! Something went wrong", errorState: true });
        // Log only a sanitized status/code — the request payload contains the
        // user-entered deletion reason and must not be sent to LogRocket.
        console.error("Error deleting document:", {
          status: error?.response?.status,
          code: error?.code,
        });
      },
    }
  );

  const { mutate: unverifyDocument, isLoading: unverifyIsLoading } = useMutation(
    (documentId: string) => unverifyClientDocument(documentId),
    {
      onSuccess: () => updateTable(),
      onError: (error, documentId) => {
        showToast({
          message: "Whoops! Something went wrong",
          errorState: true,
          retryHandler: () => unverifyDocument(documentId),
        });
        console.error("Error unverifying document:", error);
      },
    }
  );

  useEffect(() => {
    if (!uploadDocumentLoading) {
      updateTable();
      const isTreatmentPlan = uploadDocType === "TREATMENT_PLAN";
      if (workflowStatus !== "FULL_CUSTOMER" && isTreatmentPlan) {
        setRecommendedTherapyHoursOpen(true);
      }
    }
  }, [uploadDocumentLoading]);

  const viewMode = userPermissions["view_client_dashboard"];
  const orderByMap: any = {
    "uploaded-date-asc": { sort: "created", direction: "asc" },
    "uploaded-date-desc": { sort: "created", direction: "desc" },
    type: { sort: "display_name", direction: "asc" },
    "uploaded-by": { sort: "uploaded_by", direction: "asc" },
  };
  // using isFetching instead of isLoading, since keepPreviousData (avoids showing `undefined` in chips)
  // is set to true in useGetClientDocuments and isLoading will be false when
  // the previous data is being used
  const {
    isFetching,
    data: response,
    refetch: updateTable,
  } = useGetClientDocuments(clientId!, {
    sort: orderByMap[orderBy]?.sort,
    direction: orderByMap[orderBy]?.direction,
    page: paginationModel.page,
    size: paginationModel.pageSize,
    typeList: !typeFilter.length ? null : typeFilter.join(","),
    uploadedByList: uploadedByFilter.join(","),
    startDate: uploadedDateFilter?.[0],
    endDate: uploadedDateFilter?.[1],
    status: statusFilter.join(","),
  });

  const debounce = (fn: (...args: any[]) => void, delay: number) => {
    let timeout: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), delay);
    };
  };

  const debouncedUpdateTable = debounce(updateTable, 300);

  // If new filter is added make sure to add it to the prevFiltersRef
  const prevFiltersRef = useRef({
    typeFilter: typeFilter,
    uploadedByFilter: uploadedByFilter,
    uploadedDateFilter: uploadedDateFilter,
    statusFilter: statusFilter,
  });
  // If new filter is added make sure to add it this useEffect
  useEffect(() => {
    const prevFilters = prevFiltersRef.current;

    if (
      !arraysEqual(prevFilters.typeFilter, typeFilter) ||
      !arraysEqual(prevFilters.uploadedByFilter, uploadedByFilter) ||
      !arraysEqual(prevFilters.uploadedDateFilter, uploadedDateFilter) ||
      !arraysEqual(prevFilters.statusFilter, statusFilter)
    ) {
      setPaginationModel((prevPaginationModel) => ({
        ...prevPaginationModel,
        page: 0,
      }));
    }
    prevFiltersRef.current = {
      typeFilter: typeFilter,
      uploadedByFilter: uploadedByFilter,
      uploadedDateFilter: uploadedDateFilter,
      statusFilter: statusFilter,
    };

    debouncedUpdateTable();
  }, [typeFilter, uploadedByFilter, uploadedDateFilter, statusFilter]);

  const arraysEqual = (a: any, b: any) => {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (a.length !== b.length) return false;

    for (let i = 0; i < a.length; ++i) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  };

  const documents = response?.data.documents;
  // The API returns count as a string; DataGrid's rowCount (and the
  // TablePagination it renders) require a number, and `!"0"` is falsy-unsafe
  // for the hideFooter check below.
  const rowCount = Number(response?.data.count) || 0;
  const typeCount = response?.data.type_count;
  const uploadedByCount = response?.data.uploaded_by_count;
  const statusCount = response?.data.status_count;

  const handleFileUpload = (file: any) => {
    setDropzoneFiles([
      { errors: !!file.fileRejections?.[0], file: file.acceptedFiles[0] || file.fileRejections[0] },
    ]);
    if (!uploadDocumentOpen) setUploadDocumentOpen(true);
  };

  const handleFileDelete = (file: any) => {
    setDropzoneFiles(dropzoneFiles.filter((dropzoneFile) => dropzoneFile !== file));
  };

  const handleLoadedFileDelete = (file: any) => {
    setAlreadyUploadedFiles(alreadyUploadedFiles.filter((uploadedFile) => uploadedFile !== file));
  };

  const handleOptionsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setOptionsMenuAnchorEl(event.currentTarget);
  };

  const handleOptionsMenuClose = () => {
    setSelectedRow(null);
    setOptionsMenuAnchorEl(null);
  };

  const isOptionsMenuOpen = Boolean(optionsMenuAnchorEl);

  const normalizeDocTypeLabel = (value?: string) =>
    (value || "").toLowerCase().replace(/[^a-z0-9]/g, "");

  // Generated documents (multi-step verification) are identified by their register type.
  const getRegisterType = (row: any) => {
    if (!row) return undefined;
    if (row.registerType) return row.registerType;

    const rowType = row?.type;
    if (!rowType) return undefined;

    const directMatch = documentTypes.find(
      (docType: any) => docType.display_name === rowType || docType.name === rowType
    );
    if (directMatch?.name) return directMatch.name;

    const normalizedRowType = normalizeDocTypeLabel(rowType);
    const normalizedMatch = documentTypes.find(
      (docType: any) => normalizeDocTypeLabel(docType.display_name) === normalizedRowType
    );

    return normalizedMatch?.name;
  };
  // Only rows initialized for multi-step verification (verification column set)
  // qualify. Manually-uploaded docs whose type happens to appear in
  // DOCUMENT_TYPE_VERIFIER_SEQUENCE have verification=null and must fall through
  // to the legacy Verify/Unverify actions.
  const isMultiStepRow = (row: any) =>
    ["UNVERIFIED", "AWAITING_SIGNATURE", "VERIFIED"].includes(row?.verification);

  const selectedIsMultiStep = selectedRow ? isMultiStepRow(selectedRow.row) : false;
  const selectedCanBeSigned =
    selectedRow?.row?.verification === "UNVERIFIED" ||
    selectedRow?.row?.verification === "AWAITING_SIGNATURE";
  const selectedCanBeDeleted = selectedIsMultiStep && selectedCanBeSigned;
  const { data: verificationStatus, isLoading: verificationStatusLoading } = useQuery(
    ["documentVerificationStatus", selectedRow?.id],
    () => getDocumentVerificationStatus(selectedRow.id),
    {
      enabled: Boolean(selectedRow && selectedIsMultiStep && selectedCanBeSigned),
    }
  );
  // Single source of truth: server AND-merges `sign_generated_document`
  // permission with the per-step role/client-link gates (see
  // documentVerificationController.getVerificationStatus). Both the menu row
  // and the URL-param auto-open key off this.
  const canSign = selectedCanBeSigned && Boolean(verificationStatus?.canCurrentUserSign);

  // Auto-open sign dialog for `?verifyDocumentId=...`, but only if the row
  // passes the same gates as the menu item.
  useEffect(() => {
    const pendingId = pendingAutoOpenIdRef.current;
    if (!pendingId || !selectedRow || String(selectedRow.id) !== pendingId) return;

    if (!selectedIsMultiStep || !selectedCanBeSigned) {
      pendingAutoOpenIdRef.current = null;
      setSelectedRow(null);
      return;
    }
    if (verificationStatusLoading || !verificationStatus) return;

    pendingAutoOpenIdRef.current = null;
    if (canSign) {
      setSignDialogOpen(true);
    } else {
      setSelectedRow(null);
    }
  }, [
    selectedRow,
    selectedIsMultiStep,
    selectedCanBeSigned,
    verificationStatus,
    verificationStatusLoading,
    canSign,
  ]);

  const handleOpenSignDialog = () => {
    setOptionsMenuAnchorEl(null);
    setSignDialogOpen(true);
  };

  const handleCloseSignDialog = () => {
    setSignDialogOpen(false);
    setSelectedRow(null);
  };

  const handleOpenDeleteDialog = () => {
    setOptionsMenuAnchorEl(null);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedRow(null);
  };

  const handleSignDocument = async ({ dataUrl }: { dataUrl: string }) => {
    if (isSigning) return;
    setIsSigning(true);
    try {
      await signDocumentVerification(selectedRow.id, { signatureDataUrl: dataUrl });
      showToast({ message: "Document Signed" });
      updateTable();
      // The clinician's signature clears any override server-side.
      queryClient.invalidateQueries(["documentChecklistStatus", clientId]);
    } catch (error: any) {
      const status = error?.response?.status;
      const message =
        status === 403
          ? "You can't sign this document right now — it may not be your turn or it was already signed."
          : status === 400
            ? "This document is already fully verified."
            : "Whoops! Something went wrong";
      showToast({ message, errorState: true });
      console.error("Error signing document:", error);
    } finally {
      setIsSigning(false);
      handleCloseSignDialog();
    }
  };

  const handleVerifyDocument = () => {
    setOptionsMenuAnchorEl(null);
    verifyDocument(selectedRow.id);
  };

  const handleUnverifyDocument = () => {
    setOptionsMenuAnchorEl(null);
    unverifyDocument(selectedRow.id);
  };

  const handleEdit = () => {
    const { row } = selectedRow;
    const registerType = getRegisterType(row);
    if (!registerType) {
      setOptionsMenuAnchorEl(null);
      showToast({
        message: "This document type can't be edited here.",
        errorState: true,
      });
      return;
    }

    setUploadDocumentOpen(true);
    setIsNewDocument(false);
    setAlreadyUploadedFiles([{ name: row.name, key: row.name, size: row.file_size ?? 0 }]);
    setUploadDocType(registerType);
    setOptionsMenuAnchorEl(null);
  };

  const submitDocumentUpload = async (uploadDocType: string) => {
    const uploadDoc = dropzoneFiles[0]?.file;
    const { details } = customerDetails;
    // if there is a new file or updated file to upload
    let upload;
    if (uploadDoc) {
      const fileUUID = uuidv4();
      const [fileName, fileExtension] = uploadDoc.name.split(".");
      const response = await uploadUserDocumentToS3Async({
        file: uploadDoc,
        key: isNewDocument
          ? `${details.cognito_id}/${fileName}-${fileUUID}.${fileExtension}`
          : selectedRow?.row?.key,
        type: "USER_DOC",
        contentType: uploadDoc.type,
        acl: "authenticated-read",
      });
      upload = response.data;
    }
    if (isNewDocument) {
      const document = {
        filename: upload.Key.split("/")[1],
        key: upload.Key,
        display_filename: uploadDoc.name,
        clientId: clientId,
        type: uploadDocType,
        uploaded_by: currentUser.id,
        file_size: uploadDoc.size,
        clinicianUserId: currentUser.id, // Needed for the saga to update treatment plan
      };
      dispatch(actions.uploadCustomerDocument(document));
    } else {
      const documentData = {
        id: selectedRow.id,
        type: uploadDocType,
        filename: uploadDoc ? upload.Key.split("/")[1] : selectedRow?.row?.name,
        display_filename: uploadDoc ? uploadDoc.name : selectedRow?.row?.name,
        file_size: uploadDoc ? uploadDoc.size : selectedRow?.row?.file_size,
        uploaded_by: uploadDoc ? currentUser.id : null, // we want to change uploaded_by only if the document is being updated
      };
      updateClientDocument(documentData);
    }
  };

  const generateRows = () => {
    if (!documents) return [];
    return documents.map((document: any) => ({
      id: document.id,
      name: document.display_filename || document.filename,
      type: document.display_name,
      registerType: document.type,
      "uploaded-by": document.uploaded_by,
      "uploaded-date": document.created,
      verification: document.verification,
      verified: document.verified,
      edit: "Edit",
      file_size: document.file_size,
      key: document.key,
    }));
  };
  const rows = generateRows();

  useEffect(() => {
    const query = new URLSearchParams(search);
    const verifyDocumentId = query.get("verifyDocumentId");
    if (!verifyDocumentId || autoOpenedVerifyIdRef.current === verifyDocumentId) return;

    const stageRow = (row: any) => {
      autoOpenedVerifyIdRef.current = verifyDocumentId;
      pendingAutoOpenIdRef.current = String(row.id);
      setSelectedRow({ id: row.id, row });
    };

    const targetRow = rows.find((r: any) => String(r.id) === verifyDocumentId);
    if (targetRow) {
      stageRow(targetRow);
      return;
    }

    // Fallback: document isn't on the current page — fetch it individually.
    let cancelled = false;
    (async () => {
      try {
        const res = await getClientDocumentById(verifyDocumentId);
        const doc = res?.data?.document ?? res?.data ?? res;
        if (cancelled || !doc?.id) return;
        stageRow({
          id: doc.id,
          name: doc.display_filename || doc.filename,
          type: doc.display_name,
          registerType: doc.type,
          "uploaded-by": doc.uploaded_by,
          "uploaded-date": doc.created,
          verification: doc.verification,
          verified: doc.verified,
          edit: "Edit",
          file_size: doc.file_size,
          key: doc.key,
        });
      } catch (err) {
        console.error("Failed to fetch document for auto-open:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [search, documents]);

  const getColumnDefinitions = (handleOpenFile: (params: any) => void) => {
    const DateValueGetter = ({ value }: any) => (value ? format(new Date(value), "P") : null);
    const defaultColAttrs = {
      sortable: false,
      flex: 1,
    };
    const columns: any[] = [
      {
        field: "name",
        headerName: "Name",
        ...defaultColAttrs,
        renderCell: (params: any) => {
          return (
            <Link
              color="primary"
              variant="body2"
              underline="hover"
              onClick={() => handleOpenFile(params)}
              sx={{ "&:hover": { cursor: "pointer" } }}
            >
              {params.value}
            </Link>
          );
        },
      },
      { field: "type", headerName: "Type", ...defaultColAttrs },
      { field: "uploaded-by", headerName: "Uploaded By", ...defaultColAttrs },
      {
        field: "uploaded-date",
        headerName: "Uploaded Date",
        align: "left",
        headerAlign: "left",
        valueGetter: DateValueGetter,
        ...defaultColAttrs,
      },
      {
        field: "verified",
        headerName: "Status",
        ...defaultColAttrs,
        renderCell: (params: any) => {
          const verification = params.row.verification;
          const legacyVerified = params.row.verified;

          if (verification === "VERIFIED") {
            return <Chip label="Verified" variant="outlined" size="small" color="success" />;
          }
          if (verification === "AWAITING_SIGNATURE") {
            return (
              <Chip label="Awaiting Signatures" variant="outlined" size="small" color="warning" />
            );
          }
          if (verification === "UNVERIFIED") {
            return <Chip label="Unverified" variant="outlined" size="small" color="default" />;
          }

          return legacyVerified ? (
            <Chip label="Verified" variant="outlined" size="small" color="success" />
          ) : (
            <Chip label="Unverified" variant="outlined" size="small" color="default" />
          );
        },
      },
    ];
    if (!viewMode) {
      columns.push({
        field: "edit",
        headerName: "More",
        align: "left",
        headerAlign: "left",
        ...defaultColAttrs,
        renderCell: (params: any) => {
          if (isMultiStepRow(params.row) && params.row.verification === "VERIFIED") {
            return null;
          }
          return (
            <IconButton
              size="medium"
              aria-label="More actions"
              onClick={(e) => {
                setSelectedRow(params);
                handleOptionsMenuOpen(e);
              }}
            >
              <DotsThree />
            </IconButton>
          );
        },
      });
    }
    return columns;
  };

  const orderingMenuItems = [
    {
      label: "Type",
      value: "type",
      icon: <Files weight="duotone" />,
    },
    { label: "Uploaded By", value: "uploaded-by", icon: <UserCircle weight="duotone" /> },
    {
      label: "Uploaded Date",
      value: "uploaded-date-asc",
      icon: <Calendar weight="duotone" />,
      caption: "Ascending",
    },
    {
      label: "Uploaded Date",
      value: "uploaded-date-desc",
      icon: <Calendar weight="duotone" />,
      caption: "Descending",
    },
  ];
  const filtersMenuItems = [
    {
      label: "Type",
      value: "type",
      icon: <Files weight="duotone" />,
      countObject: typeCount,
      state: typeFilter,
      setState: setTypeFilter,
    },
    {
      label: "Uploaded Date",
      value: "uploaded-date",
      icon: <Calendar weight="duotone" />,
      customComponent: (
        <StaticDateRangePicker
          value={uploadedDateFilter}
          onChange={(date: any) => setUploadedDateFilter(date)}
          slotProps={{ actionBar: { actions: [] }, toolbar: { hidden: true } }}
          calendars={2}
        />
      ),
      filterChipProps: {
        label: `Uploaded Date is : ${format(new Date(uploadedDateFilter[0]!), "MM/dd/yy")}
          ${
            uploadedDateFilter[1] ? ` - ${format(new Date(uploadedDateFilter[1]), "MM/dd/yy")}` : ""
          }`,
        onDelete: () => setUploadedDateFilter([null, null]),
      },
      showChipConditionCustomComponent: !!uploadedDateFilter[0],
    },
    {
      label: "Uploaded By",
      value: "uploaded-by",
      icon: <UserCircle weight="duotone" />,
      countObject: uploadedByCount,
      state: uploadedByFilter,
      setState: setUploadedByFilter,
    },
    {
      label: "Status",
      value: "verified",
      icon: <Pulse weight="duotone" />,
      countObject: statusCount,
      state: statusFilter,
      setState: setStatusFilter,
    },
  ];

  const handleClearFilters = () => {
    setTypeFilter([]);
    setUploadedByFilter([]);
    setUploadedDateFilter([null, null]);
    setStatusFilter([]);
  };

  const customNoRowsOverlay: any = {
    NoDocuments: {
      component: ANNoDataOverlay,
      height: "296px",
      props: {
        imgSrc: noDocumentSvg,
        title: "No documents yet",
        description: "Client documents will display here once uploaded to the account",
      },
    },
    NoDocumentsWithPermission: {
      component: NoDocumentsWithPermissionOverlay,
      height: "296px",
      props: { handleFileUpload },
    },
    NoResults: {
      component: ANNoResultsOverlay,
      height: "236px",
      props: { handleClearFilters },
    },
  };
  const overlayName =
    !typeFilter.length &&
    !uploadedByFilter.length &&
    !uploadedDateFilter[0] &&
    !uploadedDateFilter[1] &&
    !statusFilter.length
      ? viewMode
        ? "NoDocuments"
        : "NoDocumentsWithPermission"
      : "NoResults";

  return (
    <>
      <Stack
        direction="row"
        padding={theme.spacing(5)}
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <ANTableFilters
          orderingMenuItems={orderingMenuItems}
          orderBy={orderBy}
          setOrderBy={setOrderBy}
          filtersMenuItems={filtersMenuItems}
          filtersButtonProps={{ disabled: documents?.length === 0 }}
        />
        {!viewMode && (
          <Stack direction="row" gap={2}>
            <Button
              color="secondary"
              startIcon={<UploadSimple />}
              size="small"
              onClick={(event) => {
                event.currentTarget.blur();
                setUploadDocumentOpen(true);
              }}
              sx={{ minWidth: "fit-content" }}
            >
              Upload Document
            </Button>
            <Button
              color="secondary"
              startIcon={<Sparkle weight="duotone" />}
              size="small"
              onClick={(event) => {
                event.currentTarget.blur();
                setGenerateDocumentOpen(true);
              }}
              sx={{ minWidth: "fit-content" }}
            >
              Generate Document
            </Button>
            {canGrantOverride && (
              <IconButton
                variant="outlined"
                color="secondary"
                size="small"
                fontSize="small"
                aria-label="Document options"
                onClick={(event) => setSettingsMenuAnchorEl(event.currentTarget)}
                sx={{ borderRadius: "10px" }}
              >
                <DotsThree />
              </IconButton>
            )}
          </Stack>
        )}
      </Stack>
      <DataGrid
        rows={rows}
        loading={
          (isFetching && !documents) ||
          isLoadingDocumentUpdate ||
          uploadDocumentLoading ||
          verifyIsLoading ||
          unverifyIsLoading ||
          isSigning
        }
        pageSizeOptions={[5, 10, 20]}
        columns={getColumnDefinitions(({ row }: any) => {
          const url = `/document-viewer/${userId}/${row.id}`;
          window.open(url, "_blank", "noopener,noreferrer");
        })}
        disableRowSelectionOnClick
        autoHeight
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        slots={{
          noRowsOverlay: customNoRowsOverlay[overlayName].component,
        }}
        slotProps={{ noRowsOverlay: { ...customNoRowsOverlay[overlayName].props } }}
        sx={{ "--DataGrid-overlayHeight": customNoRowsOverlay[overlayName].height }}
        hideFooter={!rowCount}
        paginationMode="server"
        rowCount={rowCount}
      />
      <UploadDocumentDialog
        open={uploadDocumentOpen}
        handleClose={() => {
          setUploadDocumentOpen(false);
          setSelectedRow(null);
          setUploadDocType("");
          setTimeout(() => {
            setIsNewDocument(true);
            setAlreadyUploadedFiles([]);
            setDropzoneFiles([]);
          }, 250); // Timeout avoids flickering
        }}
        handleFileUpload={handleFileUpload}
        dropzoneFiles={dropzoneFiles}
        handleFileDelete={handleFileDelete}
        handleLoadedFileDelete={handleLoadedFileDelete}
        submitDocumentUpload={submitDocumentUpload}
        isNewDocument={isNewDocument}
        alreadyUploadedFiles={alreadyUploadedFiles}
        uploadDocType={uploadDocType}
        setUploadDocType={setUploadDocType}
      />
      <GenerateDocumentDialog
        open={generateDocumentOpen}
        onClose={() => setGenerateDocumentOpen(false)}
        clientId={clientId!}
        userId={userId}
        clientName={`${client?.name || ""} ${client?.child_last_name || ""}`.trim()}
      />
      <RecommendedTherapyHoursDialog
        clientId={clientId!}
        onClose={() => setRecommendedTherapyHoursOpen(false)}
        open={recommendedTherapyHoursOpen}
      />
      <Menu
        anchorEl={settingsMenuAnchorEl}
        open={Boolean(settingsMenuAnchorEl)}
        onClose={() => setSettingsMenuAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setSettingsMenuAnchorEl(null);
            setDocumentSettingsOpen(true);
          }}
        >
          <ListItemIcon>{<FileLock weight="duotone" />}</ListItemIcon>
          Document Settings
        </MenuItem>
      </Menu>
      <DocumentSettingsDialog
        open={documentSettingsOpen}
        onClose={() => setDocumentSettingsOpen(false)}
        clientId={clientId!}
        clientFirstName={client?.name}
        clientLastName={client?.child_last_name}
        isLoading={statusesLoading}
        activeOverride={activeOverride}
      />
      <Menu
        anchorEl={optionsMenuAnchorEl}
        open={isOptionsMenuOpen}
        onClose={handleOptionsMenuClose}
      >
        {selectedIsMultiStep ? (
          [
            <MenuItem
              key={`${selectedRow?.id}_verify_document`}
              disabled={verificationStatusLoading || !canSign}
              onClick={handleOpenSignDialog}
            >
              <ListItemIcon>{<SealCheck weight="duotone" />}</ListItemIcon>
              Verify Document
            </MenuItem>,
            <MenuItem
              key={`${selectedRow?.id}_delete_document`}
              disabled={!selectedCanBeDeleted || !userPermissions[DELETE_DOCUMENT]}
              onClick={handleOpenDeleteDialog}
            >
              <ListItemIcon>{<XCircle weight="duotone" />}</ListItemIcon>
              Delete Document
            </MenuItem>,
          ]
        ) : (
          [
            selectedRow?.row?.verified ? (
              <MenuItem
                key={`${selectedRow?.id}_verify_document`}
                disabled={!userPermissions.verify_client_document}
                onClick={handleUnverifyDocument}
              >
                <ListItemIcon>{<SealCheck weight="duotone" />}</ListItemIcon>
                Cancel Verification
              </MenuItem>
            ) : (
              <MenuItem
                key={`${selectedRow?.id}_verify_document`}
                disabled={!userPermissions.verify_client_document}
                onClick={handleVerifyDocument}
              >
                <ListItemIcon>{<SealCheck weight="duotone" />}</ListItemIcon>
                Verify Document
              </MenuItem>
            ),
            <MenuItem key={`${selectedRow?.id}_handle_edit`} onClick={handleEdit}>
              <ListItemIcon>{<PencilSimpleLine weight="duotone" />}</ListItemIcon>
              Edit
            </MenuItem>,
          ]
        )}
      </Menu>
      <SignDocumentDialog
        documentTitle={selectedRow?.row?.type || "Document"}
        clientName={`${client?.name || ""} ${client?.child_last_name || ""}`.trim()}
        dateGenerated={
          selectedRow?.row?.["uploaded-date"]
            ? format(new Date(selectedRow.row["uploaded-date"]), "P")
            : ""
        }
        attestations={
          ["SUPERVISOR", "DIRECTOR"].includes(verificationStatus?.nextVerifierRole || "")
            ? []
            : DOCUMENT_TYPE_ATTESTATIONS[getRegisterType(selectedRow?.row) || ""] || []
        }
        open={signDialogOpen}
        onClose={handleCloseSignDialog}
        onSign={handleSignDocument}
      />
      <DeleteDocumentDialog
        open={deleteDialogOpen}
        isSubmitting={isDeleting}
        onClose={handleCloseDeleteDialog}
        onConfirm={(reason) => deleteDocumentMutation(reason)}
      />
    </>
  );
};

export default Documents;
